<script lang="ts">
import { stripHtmlForSchema } from '$lib/utils/string.js';
import { ETitleFontSizes, ETitleTextColCtaStyles } from '../../../config/enums.js';
import Link from '../../atoms/Link/Link.svelte';

type TitleTextColCtaStyle = 'Text Link' | 'Black Button';

interface TitleTextColProps {
    title?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    textList?: {
        text?: string;
        link?: {
            to: string;
            text: string;
            ctaStyle?: TitleTextColCtaStyle;
        };
    }[];
    hideBlock?: boolean;
}

let {
    title = '',
    headingLevel = 'h2',
    titleSize = ETitleFontSizes.H2,
    textList = [],
    hideBlock = false,
}: TitleTextColProps = $props();

const titleSizeClassName = $derived(`title-text-col__title--${titleSize}`);

function getLinkClass(ctaStyle?: TitleTextColCtaStyle) {
    return ctaStyle === ETitleTextColCtaStyles.BLACK_BUTTON
        ? 'button button--inverse'
        : 'link-text';
}

function getPlainText(text?: string) {
    if (!text) return '';

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(text);

    return hasHtmlTags ? stripHtmlForSchema(text.replace(/>\s*</g, '> <')) : text;
}
</script>

{#if !hideBlock}
    <div class="title-text-col" data-aos="fade-up">
        <div class="title-text-col__col">
            {#if title}
                <svelte:element this={headingLevel} class="title-text-col__title {titleSizeClassName}">
                    {title}
                </svelte:element>
            {/if}
        </div>
        <div class="title-text-col__col">
            {#each textList as textItem, index (index)}
                <div class="title-text-col__content">
                    <p class="title-text-col__text">{getPlainText(textItem.text)}</p>
                    {#if textItem.link}
                        <div>
                            <Link
                                to={textItem.link.to}
                                text={textItem.link.text}
                                type="link"
                                class={getLinkClass(textItem.link.ctaStyle)}
                            />
                        </div>
                    {/if}
                </div>
            {/each}
        </div>
    </div>
{/if}

<style lang="scss">
    @use "/src/scss/global-constants/typography";
    @use "sass-mq/mq" as *;

    .title-text-col {
        display: flex;
        flex-direction: column;
        gap: 2.5rem;
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);

        @include mq($from: tablet) {
            flex-direction: row;
            gap: 5.5rem;
        }

        @include mq($from: desktop) {
            display: grid;
            grid-template-columns: repeat(var(--layout-pagegrid-col-count), 1fr);
            column-gap: var(--layout-pagegrid-gutter);
        }

        &__col {
            display: flex;
            gap: 4.8rem;
            flex-direction: column;
            flex: 1;

            @include mq($from: desktop) {
                &:first-child {
                    grid-column: 1 / 5;
                }

                &:nth-child(2) {
                    grid-column: 5 / -1;
                }
            }
        }

        &__content {
            gap: 2rem;
            display: flex;
            flex-direction: column;
        }

        &__text {
            @include typography.title-text-col-body;
        }

        &__title {
            &--h1 {
                @include typography.display-xl;
            }

            &--h1-5 {
                @include typography.display-hero;
            }

            &--h2 {
                @include typography.h2;
            }

            &--h3 {
                @include typography.display-md;
            }

            &--h4 {
                @include typography.heading-lg;
            }

            &--h5 {
                @include typography.heading-md;
            }
        }
    }
</style>
